import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { Button } from "@/components/ui/button";
import { BellRing, X as XIcon, Loader2 } from "lucide-react";

const DISMISS_KEY = "popoyo_push_prompt_dismissed_at";
const DISMISS_TTL_DAYS = 7;

/**
 * Auto-prompt banner that nudges signed-in users to enable push alerts —
 * because adding Popoyo.app to the home-screen on iOS/Android does NOT
 * automatically grant Notification permission. Without this, users install
 * the PWA and are silently missing emergency alerts (earthquake, tsunami,
 * hurricane, admin broadcast). Shows once, dismissible for 7 days.
 */
export default function PushAlertsPrompt() {
  const { user } = useAuth();
  const { supported, permission, subscribed, busy, subscribe } = usePushNotifications();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Skip if browser doesn't support push, user already subscribed, blocked,
    // or dismissed within the TTL. Also skip if not signed in.
    if (!user?.id || !supported || subscribed || permission === "denied") {
      setVisible(false);
      return;
    }
    let dismissed = 0;
    try {
      dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    } catch (_e) { /* private mode */ }
    const ttlMs = DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
    if (dismissed && Date.now() - dismissed < ttlMs) return;
    // Wait 4s after load so it doesn't fight for attention with the initial
    // hero paint.
    const timer = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(timer);
  }, [user?.id, supported, subscribed, permission]);

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* ignore */ }
    setVisible(false);
  };

  const enable = async () => {
    await subscribe();
    // subscribe() updates `subscribed` — the effect above will hide the banner
    // on the next render. Belt & suspenders:
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* ignore */ }
  };

  return (
    <div
      data-testid="push-alerts-prompt"
      role="dialog"
      aria-live="polite"
      className="fixed z-[1200] left-3 right-3 sm:left-auto sm:right-4 sm:w-[380px] rounded-2xl shadow-[0_20px_60px_-20px_rgba(15,35,64,0.45)] border border-[color:var(--coral)]/30 overflow-hidden"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
        background: "linear-gradient(135deg, #1B3B6F 0%, #0F2340 100%)",
      }}
    >
      <div className="p-4 sm:p-5 text-white relative">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss for now"
          data-testid="push-alerts-prompt-close"
          className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/10"
        >
          <XIcon className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: "var(--coral)" }}
          >
            <BellRing className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--coral)]">
              Emergency alerts
            </p>
            <h4 className="font-bold text-base leading-tight mt-0.5" style={{ fontFamily: "Outfit" }}>
              Turn on earthquake & tsunami alerts
            </h4>
            <p className="text-xs text-white/80 mt-1.5">
              Installing Popoyo.app to your home screen doesn't turn on push alerts on its own — you have to allow notifications too. One tap and you're covered.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Button
                onClick={enable}
                disabled={busy}
                data-testid="push-alerts-prompt-enable"
                className="pill bg-[color:var(--coral)] text-white hover:opacity-90"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <BellRing className="w-4 h-4 mr-1.5" />}
                Enable alerts
              </Button>
              <button
                type="button"
                onClick={dismiss}
                data-testid="push-alerts-prompt-later"
                className="text-xs font-semibold text-white/70 hover:text-white px-2 py-1.5"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
